import React from 'react';
import { Container } from '../../components/ui/Container';
import { Button } from '../../components/ui/Button';
import { ArrowLeft, CheckCircle, TrendingUp, AlertCircle } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
export function UnderstandingFlowHealthScoresPage() {
  const navigate = useNavigate();
  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/resources');
    }
  };
  return <div className="bg-white" data-id="element-1577">
      {/* Header */}
      <div className="bg-gray-900 py-12 lg:py-16" data-id="element-1578">
        <Container data-id="element-1579">
          <button onClick={handleGoBack} className="flex items-center text-gray-300 hover:text-white mb-6 transition-colors" data-id="element-1580">
            <ArrowLeft className="h-5 w-5 mr-2" data-id="element-1581" />
            Back
          </button>
          <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl mb-4" data-id="element-1582">
            Understanding Flow Health Scores
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl" data-id="element-1583">
            Learn how Flowsense calculates health scores and what each metric
            means for your email marketing performance.
          </p>
        </Container>
      </div>

      {/* Content */}
      <article className="py-16 lg:py-24" data-id="element-1584">
        <Container size="md" data-id="element-1585">
          {/* Featured Image */}
          <div className="mb-12" data-id="element-1586">
            <img src="https://cdn.magicpatterns.com/uploads/qDKhxDiBcikuyjmS5we4yd/2._understanding-flow-health.png" alt="Flow Health Score Dashboard" className="w-full rounded-lg shadow-xl" data-id="element-1587" />
          </div>

          {/* Introduction */}
          <div className="prose prose-lg max-w-none mb-12" data-id="element-1588">
            <p className="text-xl text-gray-600 leading-relaxed" data-id="element-1589">
              Flow health scores are the foundation of Flowsense's AI-powered
              optimization system. Every flow in your Klaviyo account receives a
              score from 0-100, giving you instant visibility into which flows
              need attention and which are performing well.
            </p>
          </div>

          {/* What is a Flow Health Score */}
          <section className="mb-16" data-id="element-1590">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1591">
              What is a Flow Health Score?
            </h2>
            <p className="text-lg text-gray-600 mb-6" data-id="element-1592">
              A flow health score is a calculated metric that evaluates your
              email automation across four key dimensions:
            </p>
            <div className="grid md:grid-cols-2 gap-6 mb-8" data-id="element-1593">
              <div className="bg-gray-50 p-6 rounded-lg" data-id="element-1594">
                <div className="flex items-start mb-4" data-id="element-1595">
                  <TrendingUp className="h-6 w-6 text-indigo-600 mr-3 flex-shrink-0 mt-1" data-id="element-1596" />
                  <div data-id="element-1597">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1598">
                      Performance Metrics
                    </h3>
                    <p className="text-gray-600" data-id="element-1599">
                      Open rates, click rates, conversion rates, and revenue
                      generated compared to industry benchmarks.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg" data-id="element-1600">
                <div className="flex items-start mb-4" data-id="element-1601">
                  <CheckCircle className="h-6 w-6 text-green-600 mr-3 flex-shrink-0 mt-1" data-id="element-1602" />
                  <div data-id="element-1603">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1604">
                      Structure Quality
                    </h3>
                    <p className="text-gray-600" data-id="element-1605">
                      Flow logic, timing, delays, splits, and conditional paths
                      evaluated against best practices.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg" data-id="element-1606">
                <div className="flex items-start mb-4" data-id="element-1607">
                  <AlertCircle className="h-6 w-6 text-purple-600 mr-3 flex-shrink-0 mt-1" data-id="element-1608" />
                  <div data-id="element-1609">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1610">
                      Content Optimization
                    </h3>
                    <p className="text-gray-600" data-id="element-1611">
                      Subject lines, body copy, CTAs, images, and
                      personalization tokens analyzed for effectiveness.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg" data-id="element-1612">
                <div className="flex items-start mb-4" data-id="element-1613">
                  <CheckCircle className="h-6 w-6 text-blue-600 mr-3 flex-shrink-0 mt-1" data-id="element-1614" />
                  <div data-id="element-1615">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1616">
                      Best Practice Compliance
                    </h3>
                    <p className="text-gray-600" data-id="element-1617">
                      Adherence to email marketing standards, accessibility
                      guidelines, and deliverability requirements.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Score Ranges */}
          <section className="mb-16" data-id="element-1618">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1619">
              Understanding Score Ranges
            </h2>
            <p className="text-lg text-gray-600 mb-6" data-id="element-1620">
              Health scores are color-coded to help you quickly identify which
              flows need immediate attention:
            </p>
            <div className="space-y-4 mb-8" data-id="element-1621">
              <div className="flex items-start p-4 bg-red-50 border-l-4 border-red-500 rounded" data-id="element-1622">
                <div className="flex-grow" data-id="element-1623">
                  <h3 className="text-lg font-semibold text-red-900 mb-2" data-id="element-1624">
                    0-49: Critical (Red)
                  </h3>
                  <p className="text-red-700" data-id="element-1625">
                    Flows with critical issues that are likely costing you
                    significant revenue. These should be your top priority for
                    optimization.
                  </p>
                </div>
              </div>
              <div className="flex items-start p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded" data-id="element-1626">
                <div className="flex-grow" data-id="element-1627">
                  <h3 className="text-lg font-semibold text-yellow-900 mb-2" data-id="element-1628">
                    50-74: Needs Improvement (Amber)
                  </h3>
                  <p className="text-yellow-700" data-id="element-1629">
                    Flows that are functional but have clear opportunities for
                    improvement. Addressing these issues will drive measurable
                    gains.
                  </p>
                </div>
              </div>
              <div className="flex items-start p-4 bg-green-50 border-l-4 border-green-500 rounded" data-id="element-1630">
                <div className="flex-grow" data-id="element-1631">
                  <h3 className="text-lg font-semibold text-green-900 mb-2" data-id="element-1632">
                    75-100: Healthy (Green)
                  </h3>
                  <p className="text-green-700" data-id="element-1633">
                    Well-optimized flows following best practices. Continue
                    monitoring for minor refinements and A/B testing
                    opportunities.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* How Scores Are Calculated */}
          <section className="mb-16" data-id="element-1634">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1635">
              How Scores Are Calculated
            </h2>
            <p className="text-lg text-gray-600 mb-6" data-id="element-1636">
              Flowsense uses a weighted algorithm that considers multiple
              factors:
            </p>
            <ul className="space-y-4 mb-8" data-id="element-1637">
              <li className="flex items-start" data-id="element-1638">
                <CheckCircle className="h-6 w-6 text-success mr-3 flex-shrink-0 mt-1" data-id="element-1639" />
                <div data-id="element-1640">
                  <span className="font-semibold text-gray-900" data-id="element-1641">
                    Performance Data (40%):
                  </span>
                  <span className="text-gray-600" data-id="element-1642">
                    {' '}
                    Historical metrics from the last 30-90 days, including
                    sends, opens, clicks, conversions, and revenue.
                  </span>
                </div>
              </li>
              <li className="flex items-start" data-id="element-1643">
                <CheckCircle className="h-6 w-6 text-success mr-3 flex-shrink-0 mt-1" data-id="element-1644" />
                <div data-id="element-1645">
                  <span className="font-semibold text-gray-900" data-id="element-1646">
                    Structure Analysis (30%):
                  </span>
                  <span className="text-gray-600" data-id="element-1647">
                    {' '}
                    Flow architecture, timing strategies, conditional logic, and
                    automation complexity evaluated against proven patterns.
                  </span>
                </div>
              </li>
              <li className="flex items-start" data-id="element-1648">
                <CheckCircle className="h-6 w-6 text-success mr-3 flex-shrink-0 mt-1" data-id="element-1649" />
                <div data-id="element-1650">
                  <span className="font-semibold text-gray-900" data-id="element-1651">
                    Content Quality (20%):
                  </span>
                  <span className="text-gray-600" data-id="element-1652">
                    {' '}
                    AI analysis of subject lines, preview text, body copy, CTAs,
                    images, and personalization effectiveness.
                  </span>
                </div>
              </li>
              <li className="flex items-start" data-id="element-1653">
                <CheckCircle className="h-6 w-6 text-success mr-3 flex-shrink-0 mt-1" data-id="element-1654" />
                <div data-id="element-1655">
                  <span className="font-semibold text-gray-900" data-id="element-1656">
                    Best Practices (10%):
                  </span>
                  <span className="text-gray-600" data-id="element-1657">
                    {' '}
                    Compliance with email marketing standards, accessibility,
                    mobile optimization, and deliverability factors.
                  </span>
                </div>
              </li>
            </ul>
          </section>

          {/* Using Health Scores */}
          <section className="mb-16" data-id="element-1658">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1659">
              Using Health Scores to Prioritize Work
            </h2>
            <p className="text-lg text-gray-600 mb-6" data-id="element-1660">
              Health scores help you focus your optimization efforts where
              they'll have the biggest impact:
            </p>
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-8 rounded-lg mb-8" data-id="element-1661">
              <h3 className="text-xl font-semibold text-gray-900 mb-4" data-id="element-1662">
                Optimization Priority Framework
              </h3>
              <ol className="space-y-4" data-id="element-1663">
                <li className="flex items-start" data-id="element-1664">
                  <span className="flex-shrink-0 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold mr-4" data-id="element-1665">
                    1
                  </span>
                  <div data-id="element-1666">
                    <span className="font-semibold text-gray-900" data-id="element-1667">
                      Start with high-traffic, low-score flows:
                    </span>
                    <span className="text-gray-700" data-id="element-1668">
                      {' '}
                      These have the biggest revenue opportunity since they
                      reach many customers but aren't performing well.
                    </span>
                  </div>
                </li>
                <li className="flex items-start" data-id="element-1669">
                  <span className="flex-shrink-0 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold mr-4" data-id="element-1670">
                    2
                  </span>
                  <div data-id="element-1671">
                    <span className="font-semibold text-gray-900" data-id="element-1672">
                      Focus on quick wins:
                    </span>
                    <span className="text-gray-700" data-id="element-1673">
                      {' '}
                      Address high-priority recommendations that can be
                      implemented quickly for immediate impact.
                    </span>
                  </div>
                </li>
                <li className="flex items-start" data-id="element-1674">
                  <span className="flex-shrink-0 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold mr-4" data-id="element-1675">
                    3
                  </span>
                  <div data-id="element-1676">
                    <span className="font-semibold text-gray-900" data-id="element-1677">
                      Monitor score changes:
                    </span>
                    <span className="text-gray-700" data-id="element-1678">
                      {' '}
                      Track how scores improve after implementing
                      recommendations to validate your optimization efforts.
                    </span>
                  </div>
                </li>
                <li className="flex items-start" data-id="element-1679">
                  <span className="flex-shrink-0 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold mr-4" data-id="element-1680">
                    4
                  </span>
                  <div data-id="element-1681">
                    <span className="font-semibold text-gray-900" data-id="element-1682">
                      Set score goals:
                    </span>
                    <span className="text-gray-700" data-id="element-1683">
                      {' '}
                      Aim to bring all critical flows above 50, then
                      systematically work toward 75+ across your entire
                      portfolio.
                    </span>
                  </div>
                </li>
              </ol>
            </div>
          </section>

          {/* Related Guides */}
          <section className="border-t border-gray-200 pt-12" data-id="element-1684">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1685">
              Related Guides
            </h2>
            <div className="grid md:grid-cols-2 gap-6" data-id="element-1686">
              <Link to="/guides/implementing-ai-recommendations" className="block p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-1687">
                <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1688">
                  Implementing AI Recommendations
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-1689">
                  Learn how to prioritize and implement Flowsense
                  recommendations for maximum ROI.
                </p>
                <span className="text-indigo-600 font-medium" data-id="element-1690">
                  Read Guide →
                </span>
              </Link>
              <Link to="/guides/agency-white-label-setup" className="block p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-1691">
                <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1692">
                  Agency White-Label Setup
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-1693">
                  Complete guide to setting up custom domains and branded
                  reports for your agency.
                </p>
                <span className="text-indigo-600 font-medium" data-id="element-1694">
                  Read Guide →
                </span>
              </Link>
            </div>
          </section>

          {/* CTA */}
          <div className="mt-16 bg-gray-900 rounded-2xl p-8 lg:p-12 text-center" data-id="element-1695">
            <h2 className="text-2xl font-bold text-white mb-4" data-id="element-1696">
              Ready to Optimize Your Flows?
            </h2>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto" data-id="element-1697">
              Start analyzing your flow health scores today and discover
              opportunities to improve your email marketing performance.
            </p>
            <a href="https://go.useflowsense.com/signup" data-id="element-1698">
              <Button size="lg" data-id="element-1699">Get Started Free</Button>
            </a>
          </div>
        </Container>
      </article>
    </div>;
}